import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useNarrative } from '../contexts/NarrativeContext'
import LocationContextSelector from '../components/narrative/LocationContextSelector'
import LoadingSpinner from '../components/common/LoadingSpinner'
import {
  generateExecutiveSummary,
  generateKeyStats,
} from '../utils/narrativeTemplates'
import {
  calculateKeyStatistics,
  filterFacilitiesByLocation,
} from '../utils/narrativeDataUtils'
import { calculateEducationKeyStatistics } from '../utils/educationNarrativeDataUtils'
import {
  generateEducationExecutiveSummary,
  generateEducationKeyStats,
} from '../utils/educationNarrativeTemplates'
import {
  filterLocationsByNarratives,
  hasNarrative,
  getDefaultLocationForCategory,
} from '../utils/narrativeRegistry'

// Category-specific content components
import HealthNarrativeContent from '../components/narrative/HealthNarrativeContent'
import EducationNarrativeHybrid from '../components/narrative/EducationNarrativeHybrid'
import HealthFacilityMap from '../components/narrative/charts/HealthFacilityMap'
import EducationFacilityMap from '../components/narrative/charts/EducationFacilityMap'
import KeyInsightsCards from '../components/narrative/KeyInsightsCards'

/**
 * Narrative Dashboard Page - Data-First Approach
 *
 * Always shows facility data visualizations
 * AI narratives enhance when available (optional)
 */
function NarrativeDashboard() {
  const { locationId, storyId } = useParams()
  const navigate = useNavigate()
  const {
    currentLocation,
    facilityData,
    narrativeData,
    loading,
    locationsData,
    allFacilities,
    activeCategory,
    changeLocation,
  } = useNarrative()

  const [activeStoryId, setActiveStoryId] = useState(
    storyId || 'healthcare-access'
  )

  // Available story themes - health and education
  const availableStories = useMemo(
    () => [
      {
        id: 'healthcare-access',
        title: 'Healthcare Access',
        icon: '🏥',
        description: 'Healthcare infrastructure and service coverage',
        category: 'health',
      },
      {
        id: 'education-quality',
        title: 'Education Quality',
        icon: '📚',
        description: 'Education infrastructure and learning outcomes',
        category: 'education',
      },
      // Future:
      // { id: 'infrastructure-gaps', title: 'Infrastructure Gaps', icon: '🏗️' },
      // { id: 'resource-efficiency', title: 'Resource Efficiency', icon: '📊' },
    ],
    []
  )

  // Filter locations to only show those with narratives for the active category
  const filteredLocationsData = useMemo(() => {
    if (!locationsData) return null

    // Derive category from activeStoryId
    const story = availableStories.find(s => s.id === activeStoryId)
    const category = story?.category || 'health'

    return filterLocationsByNarratives(locationsData, category)
  }, [locationsData, activeStoryId, availableStories])

  // Check if current location has a narrative for the active category
  const currentLocationHasNarrative = useMemo(() => {
    if (!currentLocation) return false
    const story = availableStories.find(s => s.id === activeStoryId)
    const category = story?.category || 'health'
    return hasNarrative(currentLocation.id, category)
  }, [currentLocation, activeStoryId, availableStories])

  // Load narrative when location or story changes
  useEffect(() => {
    // Wait for locations data to be loaded before attempting to change location
    if (locationId && locationsData && allFacilities) {
      // Derive category from activeStoryId
      const story = availableStories.find(s => s.id === activeStoryId)
      const category = story?.category || 'health'

      // Check if we have the necessary facilities data for the category
      const hasFacilitiesData =
        category === 'health'
          ? allFacilities.health?.length > 0
          : allFacilities.education?.length > 0

      if (!hasFacilitiesData) {
        // Data not ready yet, wait
        return
      }

      // Reload if location changed OR if category changed
      const locationChanged = locationId !== currentLocation?.id
      const categoryChanged = category !== activeCategory

      if (locationChanged || categoryChanged) {
        changeLocation(locationId, activeStoryId, category)
      }
    }
  }, [
    locationId,
    activeStoryId,
    currentLocation,
    activeCategory,
    locationsData,
    allFacilities,
    changeLocation,
    availableStories,
  ])

  // Update URL when location changes
  useEffect(() => {
    if (currentLocation && !locationId) {
      navigate(`/narrative/${currentLocation.id}/${activeStoryId}`, {
        replace: true,
      })
    }
  }, [currentLocation, locationId, activeStoryId, navigate])

  // Handle location change from selector
  const handleLocationChange = newLocationId => {
    navigate(`/narrative/${newLocationId}/${activeStoryId}`)
  }

  // Handle story tab change
  const handleStoryChange = newStoryId => {
    setActiveStoryId(newStoryId)
    if (!currentLocation) return // Guard against null location

    const story = availableStories.find(s => s.id === newStoryId)
    const category = story?.category || 'health'

    // Check if current location has narrative for new category
    if (!hasNarrative(currentLocation.id, category)) {
      // Redirect to default location for this category
      const defaultLocation = getDefaultLocationForCategory(category)
      if (defaultLocation) {
        navigate(`/narrative/${defaultLocation}/${newStoryId}`)
        return
      }
    }

    changeLocation(currentLocation.id, newStoryId, category)
    navigate(`/narrative/${currentLocation.id}/${newStoryId}`)
  }

  // Loading state - show loading until initial data is ready
  const isInitialLoading =
    !locationsData ||
    !allFacilities ||
    (activeCategory === 'health' && allFacilities.health?.length === 0) ||
    (activeCategory === 'education' && allFacilities.education?.length === 0)

  if (loading || isInitialLoading) {
    return (
      <div className="bg-gray-50">
        <div className="flex items-center justify-center h-screen">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  // No facility data state (should rarely happen)
  if (!facilityData || !facilityData.metrics) {
    return (
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">
                  No Data Available
                </h3>
                <p className="text-yellow-800">
                  Facility data is not available for{' '}
                  <strong>{currentLocation?.name || locationId}</strong>. This
                  may indicate no health facilities have been registered for
                  this location.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Generate template-based content (fallback when AI narratives not available)
  // Category-aware stats and summary generation
  const isEducation = activeCategory === 'education'
  const stats = isEducation
    ? calculateEducationKeyStatistics(facilityData.metrics)
    : calculateKeyStatistics(facilityData.metrics)

  const executiveSummary =
    narrativeData?.summary?.executive_summary ||
    (isEducation
      ? generateEducationExecutiveSummary(currentLocation, stats)
      : generateExecutiveSummary(currentLocation, stats))

  const keyStats = isEducation
    ? generateEducationKeyStats(stats, currentLocation)
    : generateKeyStats(stats, currentLocation)

  // Get facilities for map (category-specific)
  const locationFacilities = isEducation
    ? allFacilities.education.filter(school => {
        if (currentLocation.type === 'district') {
          return school.district === currentLocation.name
        } else if (currentLocation.type === 'subcounty') {
          return (
            school.subcounty === currentLocation.name &&
            school.district === currentLocation.parent?.name
          )
        } else if (currentLocation.type === 'parish') {
          return school.parish === currentLocation.name
        }
        return false
      })
    : filterFacilitiesByLocation(allFacilities.health, currentLocation)

  return (
    <div className="bg-gray-50">
      {/* Location Context Selector - Shows only locations with narratives */}
      <LocationContextSelector
        currentLocation={currentLocation}
        locationsData={filteredLocationsData || locationsData}
        onLocationChange={handleLocationChange}
      />

      {/* Warning when current location has no narrative */}
      {currentLocation && !currentLocationHasNarrative && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">⚠️</span>
              <div>
                <h4 className="text-sm font-semibold text-amber-900 mb-1">
                  Limited Narrative Coverage
                </h4>
                <p className="text-sm text-amber-800">
                  Detailed narrative content is not yet available for{' '}
                  <strong>{currentLocation.name}</strong> in the{' '}
                  {activeCategory === 'education' ? 'education' : 'healthcare'}{' '}
                  category. Showing facility data only.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Story Theme Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {availableStories.map(story => (
                <button
                  key={story.id}
                  onClick={() => handleStoryChange(story.id)}
                  className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeStoryId === story.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{story.icon}</span>
                    <span>{story.title}</span>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Overview Section */}
        {activeStoryId === 'healthcare-access' && (
          <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {currentLocation?.name} -{' '}
              {isEducation
                ? 'Education Quality Overview'
                : 'Healthcare Access Overview'}
            </h2>

            {/* Executive Summary */}
            <div className="prose prose-lg max-w-none mb-6">
              <p className="text-gray-700 leading-relaxed">
                {executiveSummary}
              </p>
            </div>

            {/* Key Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {keyStats.map((stat, index) => (
                <div
                  key={index}
                  className={`rounded-lg p-4 border-2 ${
                    stat.status === 'good'
                      ? 'bg-emerald-50 border-emerald-300'
                      : stat.status === 'moderate'
                        ? 'bg-yellow-50 border-yellow-300'
                        : stat.status === 'poor'
                          ? 'bg-red-50 border-red-300'
                          : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                  }`}
                >
                  <div className="text-2xl mb-1">{stat.icon}</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Facility Map - Category-aware */}
            <div className="mt-6">
              {isEducation ? (
                <EducationFacilityMap
                  facilities={locationFacilities}
                  location={currentLocation}
                  title="School Locations"
                />
              ) : (
                <HealthFacilityMap
                  facilities={locationFacilities}
                  location={currentLocation}
                  title="Health Facility Locations"
                />
              )}
            </div>
          </div>
        )}

        {/* Key Insights Cards (Phase 1 Enhancement) */}
        <KeyInsightsCards
          narrativeData={narrativeData}
          facilityData={facilityData}
        />

        {/* Data Visualizations - Category-Specific */}
        {activeStoryId === 'healthcare-access' && (
          <HealthNarrativeContent
            facilityData={facilityData}
            narrativeData={narrativeData}
          />
        )}

        {activeStoryId === 'education-quality' && <EducationNarrativeHybrid />}

        {/* AI-Enhanced Insights (Optional Section - Only if narrativeData exists) */}
        {narrativeData &&
          narrativeData.stories?.['healthcare-access']?.sections
            ?.strategic_priorities && (
            <div className="mt-8 bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-8">
              <div className="flex items-start gap-3 mb-6">
                <span className="text-3xl">🎯</span>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Strategic Priorities & Action Plan
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    AI-generated recommendations prioritized by impact and
                    urgency
                  </p>
                </div>
              </div>

              {/* Immediate Actions */}
              {narrativeData.stories['healthcare-access'].sections
                .strategic_priorities.immediate &&
                narrativeData.stories['healthcare-access'].sections
                  .strategic_priorities.immediate.length > 0 && (
                  <div className="bg-white rounded-lg p-6 mb-4">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">🚨</span>
                      <h4 className="text-lg font-semibold text-red-900">
                        Immediate Actions (0-3 months)
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {narrativeData.stories[
                        'healthcare-access'
                      ].sections.strategic_priorities.immediate.map(
                        (item, index) => (
                          <div
                            key={index}
                            className="border-l-4 border-red-500 pl-4 py-2"
                          >
                            <p className="font-medium text-gray-900 mb-1">
                              {item.action}
                            </p>
                            <p className="text-sm text-gray-600">
                              {item.rationale}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Short-term Actions */}
              {narrativeData.stories['healthcare-access'].sections
                .strategic_priorities.short_term &&
                narrativeData.stories['healthcare-access'].sections
                  .strategic_priorities.short_term.length > 0 && (
                  <div className="bg-white rounded-lg p-6 mb-4">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">⚡</span>
                      <h4 className="text-lg font-semibold text-orange-900">
                        Short-term Priorities (3-12 months)
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {narrativeData.stories[
                        'healthcare-access'
                      ].sections.strategic_priorities.short_term.map(
                        (item, index) => (
                          <div
                            key={index}
                            className="border-l-4 border-orange-500 pl-4 py-2"
                          >
                            <p className="font-medium text-gray-900 mb-1">
                              {item.action}
                            </p>
                            <p className="text-sm text-gray-600">
                              {item.rationale}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

              {/* Strategic Actions */}
              {narrativeData.stories['healthcare-access'].sections
                .strategic_priorities.strategic &&
                narrativeData.stories['healthcare-access'].sections
                  .strategic_priorities.strategic.length > 0 && (
                  <div className="bg-white rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-2xl">🎯</span>
                      <h4 className="text-lg font-semibold text-blue-900">
                        Strategic Investments (1-2 years)
                      </h4>
                    </div>
                    <div className="space-y-3">
                      {narrativeData.stories[
                        'healthcare-access'
                      ].sections.strategic_priorities.strategic.map(
                        (item, index) => (
                          <div
                            key={index}
                            className="border-l-4 border-blue-500 pl-4 py-2"
                          >
                            <p className="font-medium text-gray-900 mb-1">
                              {item.action}
                            </p>
                            <p className="text-sm text-gray-600">
                              {item.rationale}
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}
      </div>
    </div>
  )
}

export default NarrativeDashboard
